
import { useState, useEffect } from 'react';
import Icon from '../../../components/ui/AppIcon';
import Enquiry_Form_Modal from '../../enquiry_form_modal/Enquiry_Form_Modal';

interface Challenge {
  id: string;
  text: string;
  icon: string;
  message: string;
}

const ProblemAgitation = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const challenges: Challenge[] = [
    {
      id: 'promotion',
      text: 'Stuck at the same position for years',
      icon: 'ArrowTrendingUpIcon',
      message: 'An MBA can fast-track your promotion by demonstrating management capabilities and strategic thinking skills.',
    },
    {
      id: 'skills',
      text: 'Lacking management and leadership skills',
      icon: 'UserGroupIcon',
      message: 'Our MBA curriculum focuses on practical leadership training and real-world management scenarios.',
    },
    {
      id: 'transition',
      text: 'Want to transition into management roles',
      icon: 'ArrowPathIcon',
      message: 'MBA credentials open doors to management positions across industries and functions.',
    },
    {
      id: 'salary',
      text: 'Salary growth has plateaued',
      icon: 'CurrencyRupeeIcon',
      message: 'MBA graduates see an average 40-60% salary increase within 2 years of completion.',
    },
    {
      id: 'competition',
      text: 'Facing tough competition from MBA holders',
      icon: 'TrophyIcon',
      message: 'Level the playing field with a recognized MBA degree from a UGC-approved institution.',
    },
    {
      id: 'career-break',
      text: 'Cannot afford to take a career break',
      icon: 'ClockIcon',
      message: 'Our 100% online format lets you study while working, with no career interruption required.',
    },
  ];

  const handleChallengeToggle = (challengeId: string) => {
    if (!isHydrated) return;
    
    setSelectedChallenges((prev) =>
      prev.includes(challengeId)
        ? prev.filter((id) => id !== challengeId)
        : [...prev, challengeId]
    );
  };

   const [showStickyModal, setShowStickyModal] = useState(false);

  return (
    <section id="problem-agitation" className="py-20 bg-background">
      <div className="max-w-content mx-auto px-section-margin">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Are You Facing These Career Challenges?
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Select the challenges you're experiencing. We'll show you how our Online MBA can help.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {challenges.map((challenge) => {
            const isSelected = isHydrated && selectedChallenges.includes(challenge.id);
            
            return (
              <button
                key={challenge.id}
                onClick={() => handleChallengeToggle(challenge.id)}
                className={`text-left p-6 rounded-xl border-2 transition-default ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-form'
                    : 'border-border bg-background hover:border-primary/50 hover:bg-muted'
                }`}
                style={{ minHeight: '120px' }}
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-text-secondary'
                  }`}>
                    <Icon name={challenge.icon as any} size={24} variant={isSelected ? 'solid' : 'outline'} />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-text-primary mb-2">{challenge.text}</div>
                    {isSelected && (
                      <div className="text-sm text-text-secondary">{challenge.message}</div>
                    )}
                  </div>
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'border-primary bg-primary' : 'border-border'
                  }`}>
                    {isSelected && <Icon name="CheckIcon" size={16} className="text-primary-foreground" variant="solid" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {isHydrated && selectedChallenges.length > 0 && (
          <div className="bg-accent/10 border border-accent/20 rounded-xl p-8 text-center">
            <Icon name="LightBulbIcon" size={48} className="text-accent mx-auto mb-4" variant="solid" />
            <h3 className="text-2xl font-bold text-text-primary mb-3">
              You've Selected {selectedChallenges.length} Challenge{selectedChallenges.length > 1 ? 's' : ''}
            </h3>
            <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
              Our Online MBA program is specifically designed to address these challenges. Get personalized counseling to understand how we can help you overcome these obstacles.
            </p>
            <button className="bg-accent text-accent-foreground px-8 py-4 rounded-lg font-bold shadow-cta hover:bg-accent/90 transition-default" onClick={() => {setShowStickyModal(true)}}>
              Get Personalized Career Guidance
            </button>
          </div>
        )}
      </div>

      <Enquiry_Form_Modal
          show={showStickyModal}
          onHide={() => {
            setShowStickyModal(false);
          }}
        />
    </section>
  );
};

export default ProblemAgitation;